import { ReactNode, forwardRef } from 'react';
import Link from 'next/link';
import { ADMIN_ACCENT, ADMIN_TOUCH } from './tokens';

interface BaseProps {
  variant?: 'primary' | 'secondary' | 'ghost' | 'danger';
  size?: 'sm' | 'md' | 'lg';
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  loading?: boolean;
  'aria-label'?: string;
}

interface ButtonProps extends BaseProps {
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
  href?: never;
  external?: never;
}

interface LinkProps extends BaseProps {
  href: string;
  external?: boolean;
  onClick?: never;
  type?: never;
}

type Props = ButtonProps | LinkProps;

/**
 * Admin button — consistent across all admin pages.
 *
 * Variants:
 *  - primary: filled brand color, white text — main action per page
 *  - secondary: white with border — paired with primary
 *  - ghost: text only with hover background — tertiary
 *  - danger: rose tint — destructive actions (delete, etc.)
 *
 * Sizes:
 *  - sm: 32px height — toolbar / table-row actions
 *  - md: 36-40px height — default
 *  - lg: 44px height — full-width on mobile, primary page actions
 */
export const AdminButton = forwardRef<HTMLButtonElement | HTMLAnchorElement, Props>(
  function AdminButton(props, ref) {
    const {
      variant = 'primary',
      size = 'md',
      children,
      className = '',
      disabled,
      loading,
    } = props;

    const isInactive = disabled || loading;

    const variantClasses =
      variant === 'primary'
        ? `${ADMIN_ACCENT.bg} ${ADMIN_ACCENT.bgHover} text-white shadow-sm`
        : variant === 'danger'
          ? 'bg-rose-600 text-white shadow-sm hover:bg-rose-700'
          : variant === 'ghost'
            ? 'bg-transparent text-gray-700 hover:bg-gray-100 hover:text-gray-950'
            : 'border border-gray-200 bg-white text-gray-700 shadow-sm hover:border-gray-300 hover:bg-gray-50 hover:text-gray-950';

    const sizeClasses =
      size === 'sm'
        ? 'h-8 px-3 text-xs'
        : size === 'lg'
          ? `${ADMIN_TOUCH.control} h-11 px-5 text-sm`
          : 'h-9 px-4 text-sm';

    const cls = `inline-flex items-center justify-center gap-1.5 rounded-lg font-medium transition-colors duration-150 focus-visible:outline-none focus-visible:ring-2 ${ADMIN_ACCENT.ring} focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${variantClasses} ${sizeClasses} ${className}`;

    if ('href' in props && props.href) {
      const isExternal = props.external || /^https?:\/\//.test(props.href);
      if (isExternal) {
        return (
          <a
            ref={ref as React.Ref<HTMLAnchorElement>}
            href={props.href}
            target="_blank"
            rel="noopener noreferrer"
            className={cls}
            aria-label={props['aria-label']}
          >
            {children}
          </a>
        );
      }
      return (
        <Link
          ref={ref as React.Ref<HTMLAnchorElement>}
          href={props.href}
          className={cls}
          aria-label={props['aria-label']}
        >
          {children}
        </Link>
      );
    }

    return (
      <button
        ref={ref as React.Ref<HTMLButtonElement>}
        type={(props as ButtonProps).type || 'button'}
        onClick={(props as ButtonProps).onClick}
        disabled={isInactive}
        aria-busy={loading || undefined}
        aria-label={props['aria-label']}
        className={cls}
      >
        {loading && (
          <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-current border-t-transparent" />
        )}
        {children}
      </button>
    );
  }
);
